import { getPostHogClient } from "./client";
import { isPostHogEnabled } from "./config";

/**
 * @typedef {Object} AnalyticsUser
 * @property {string|number} [id]
 * @property {string|number} [_id]
 * @property {string} [email]
 * @property {string} [name]
 * @property {string} [userStatus]
 * @property {string} [userRole]
 * @property {Record<string, unknown>} [properties]
 */

const getClient = () => {
  if (!isPostHogEnabled) return null;
  return getPostHogClient();
};

/**
 * Links anonymous activity to a known user after login / signup.
 * @param {AnalyticsUser} user
 */
export const identifyUser = (user = {}) => {
  const client = getClient();
  if (!client || !user) return;

  const distinctId = user.id ?? user._id ?? user.email;
  if (!distinctId) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "[PostHog] identifyUser skipped: missing id, _id, or email."
      );
    }
    return;
  }

  const {
    id: _idIgnored,
    _id: __idIgnored,
    email,
    name,
    userStatus,
    userRole,
    properties = {},
    ...rest
  } = user;

  client.identify(String(distinctId), {
    email,
    name,
    user_status: userStatus,
    user_role: userRole,
    ...rest,
    ...properties,
  });
};

/**
 * Captures a custom product analytics event.
 * @param {string} eventName
 * @param {Record<string, unknown>} [properties]
 */
export const captureEvent = (eventName, properties = {}) => {
  const client = getClient();
  if (!client || !eventName) return;

  client.capture(eventName, properties);
};

/**
 * Resets the distinct ID and clears person properties (call on logout).
 */
export const resetUser = () => {
  const client = getClient();
  if (!client) return;

  client.reset();
};

/**
 * Captures a pageview for the current location (used by React Router tracker).
 * @param {{ pathname?: string, search?: string, hash?: string }} [location]
 */
export const capturePageView = (location = {}) => {
  const client = getClient();
  if (!client) return;

  const pathname = location.pathname ?? window.location.pathname;
  const search = location.search ?? window.location.search;
  const hash = location.hash ?? window.location.hash;

  client.capture("$pageview", {
    $current_url: window.location.href,
    path: pathname,
    search,
    hash,
  });
};

export const analytics = {
  identifyUser,
  captureEvent,
  resetUser,
  capturePageView,
};

export default analytics;
